import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getSavedSearchById } from '~/api/search';
import DeleteSavedSearchModal from '~/components/SearchV2/DeleteSavedSearchModal/DeleteSavedSearchModal';
import DuplicateSavedSearchModal from '~/components/SearchV2/DuplicateSavedSearchModal/DuplicateSavedSearchModal';
import EditSavedSearchModal from '~/components/SearchV2/EditSavedSearchModal/EditSavedSearchModal';
import SavedSearchesTypesButtonGroup from '~/components/SearchV2/SavedSearchesTypesButtonGroup/SavedSearchesTypesButtonGroup';
import { checkSearchQueryHasUnsupportedEntity } from '~/components/SearchV2/utils';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User } from '~/components/Shared/User/User';
import { Button } from '~/eds/src/components/Button/Button';
import { QueryEntityType } from '~/enums/QueryEntityType';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import { TableContextType } from '~/enums/TableContextType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useIntl } from 'react-intl';
import {
  testIsSupportedFilterType,
  toFilters,
} from '~/redux/api/methods/search';
import { api } from '~/redux/api/createApi';
import search from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import {
  MODAL_DELETE_SAVED_SEARCH,
  MODAL_DUPLICATE_SAVED_SEARCH,
  MODAL_EDIT_SAVED_SEARCH,
} from '~/types/modal.types';
import { ERROR } from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import { testIsAdmin } from '~/utils/user';

const PAGE_SIZE = 10;

const Page = () => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const currentUser = useCurrentUser();

  const dispatch = useDispatch();
  const { data: getFiltersResponse } =
    api.endpoints.getFilterSections.useQuery(undefined);
  const fields = getFiltersResponse?.fields;

  const {
    tableSettings = {
      pageSize: PAGE_SIZE,
    },
  } = useTableSettings(TableContextType.SavedSearches);

  const [pageSize, setPageSize] = useState(tableSettings.pageSize);
  const [page, setPage] = useState(1);
  const [savedSearchesTypes, setSavedSearchesTypes] = useState([]);

  const {
    data: savedSearchesData,
    isFetching,
    error,
  } = api.endpoints.getSavedSearches.useQuery(
    { page, types: savedSearchesTypes.join(), pageSize },
    { skip: !page },
  );

  const handleSavedSearchesTypesChange = (type) => {
    setPage(1);
    setSavedSearchesTypes(type);
  };

  const handlePaginate = ({ pageIndex }) => {
    if (pageIndex !== page) {
      setPage(pageIndex);
    }
  };

  const runSearch = (searchQuery) => {
    const isComplexSearch = checkSearchQueryHasUnsupportedEntity(searchQuery);
    dispatch(search.actions.clearBooleanQuery());
    dispatch(search.actions.clearQuery());
    dispatch(search.actions.clearUnsupportedQuery());
    dispatch(search.actions.setFilters([]));

    if (isComplexSearch) {
      dispatch(search.actions.setUnsupportedQuery(searchQuery));
    } else {
      const booleanQuery =
        searchQuery.find((q) => q.entity === QueryEntityType.BoolTextSearch)
          ?.query || '';
      const supportedFieldEntities = searchQuery.filter((filter) =>
        testIsSupportedFilterType(filter),
      );
      const filters = toFilters(supportedFieldEntities);

      dispatch(search.actions.setBooleanQuery(booleanQuery));
      dispatch(search.actions.setFilters(filters));
      dispatch(search.actions.setQuery({ booleanQuery, filters, fields }));
    }
    dispatch(search.actions.setPage(1));
    navigate('/search');
  };

  const columns = [
    {
      key: 'name',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'DeprecatedSavedSearch.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Column title for saved search name',
      }),
      minWidth: 'xl',
      disableSortBy: true,
      renderCell: (savedSearchEntry) => {
        return (
          <FlexLayout>
            <Button
              sx={{ paddingLeft: 0, textAlign: 'left' }}
              text={savedSearchEntry.name}
              variant="action"
              onClick={() => runSearch(savedSearchEntry.query)}
            />
          </FlexLayout>
        );
      },
    },
    {
      key: 'created_by',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'DeprecatedSavedSearch.createdByColumnTitle',
        defaultMessage: 'Created by',
        description: 'Column title for created by',
      }),
      width: 'l',
      disableSortBy: true,
      renderCell: (searchQuery) => {
        return (
          <FlexLayout alignItems="center">
            <User mode="avatar-name" id={searchQuery.user} />
          </FlexLayout>
        );
      },
    },
    {
      key: 'created_date',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'DeprecatedSavedSearch.createdOnDateColumnTitle',
        defaultMessage: 'Created on date',
        description: 'Column title for created on date',
      }),
      width: 'l',
      disableSortBy: true,
      mapCellProps: ({ date_added }) => ({
        datetime: date_added,
      }),
    },
  ];

  function prepSavedSearchAction(row, action) {
    if (row.search_visibility !== SaveSearchPermissionType.Shared) {
      if (action === 'edit') {
        handleEdit(row);
      } else {
        handleDuplicate(row);
      }
    } else {
      loadSavedSearchRecord(row, action);
    }
  }

  function loadSavedSearchRecord(row, action) {
    getSavedSearchById(row.id)
      .then((savedSearchData) => {
        if (action === 'edit') {
          handleEdit({ ...row, ...savedSearchData });
        } else {
          handleDuplicate({ ...row, ...savedSearchData });
        }
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'DeprecatedSavedSearch.errorLoadingSavedSearchData',
            defaultMessage:
              'An error occurred while loading the saved search data',
            description:
              'Error message when loading a saved search record fails',
          }),
        );
      });
  }

  const rowActions = [
    {
      condition: (row) =>
        row.user === currentUser.id || testIsAdmin(currentUser),
      label: 'Edit',
      onClick: (row) => {
        prepSavedSearchAction(row, 'edit');
      },
    },
    {
      label: 'Duplicate',
      onClick: (row) => {
        prepSavedSearchAction(row, 'duplicate');
      },
    },
    {
      condition: (row) =>
        row.user === currentUser.id || testIsAdmin(currentUser),
      label: 'Delete',
      onClick: (row) => {
        handleDelete(row);
      },
    },
  ];

  const handleEdit = useCallback((tableState) => {
    dispatch(
      search.actions.setModal({
        modalType: MODAL_EDIT_SAVED_SEARCH,
        context: { ...tableState },
      }),
    );
  }, []);

  const handleDuplicate = useCallback((tableState) => {
    dispatch(
      search.actions.setModal({
        modalType: MODAL_DUPLICATE_SAVED_SEARCH,
        context: { ...tableState },
      }),
    );
  }, []);

  const handleDelete = useCallback((tableState) => {
    dispatch(
      search.actions.setModal({
        modalType: MODAL_DELETE_SAVED_SEARCH,
        context: { ...tableState },
      }),
    );
  }, []);

  const onPageSizeChange = useCallback((pageSize) => {
    setPage(1);
    setPageSize(pageSize);
  }, []);

  const hasResults = !error && savedSearchesData?.results.length > 0;
  const noSavedSearches = !error && savedSearchesData?.results.length === 0;

  return (
    <>
      <Box mt={3}>
        <SavedSearchesTypesButtonGroup
          types={savedSearchesTypes}
          setTypesChange={handleSavedSearchesTypesChange}
        />
      </Box>
      <LoadingContainer isLoading={isFetching}>
        {hasResults && (
          <PersistedTable
            context={TableContextType.SavedSearches}
            name={intl.formatMessage({
              id: 'DeprecatedSavedSearch.tableName',
              defaultMessage: 'saved searches',
              description: 'Name for saved searches table',
            })}
            totalCount={savedSearchesData.count}
            columns={columns}
            data={savedSearchesData.results}
            rowActions={rowActions}
            state={{ pageIndex: page, pageSize }}
            onPaginate={handlePaginate}
            onPageSizeChange={onPageSizeChange}
            options={{
              enableExportXlsx: false,
              enableSelectRows: false,
              enablePageSizeSelect: true,
            }}
          />
        )}
        {noSavedSearches && (
          <FlexLayout
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100px' }}
          >
            <Text variant="subtitle">
              {intl.formatMessage({
                id: 'DeprecatedSavedSearch.noSavedSearches',
                defaultMessage: 'No Saved Searches',
                description: 'Message shown when there are no saved searches',
              })}
            </Text>
          </FlexLayout>
        )}
        {error && (
          <FlexLayout
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100px' }}
          >
            <Text variant="subtitle">
              {intl.formatMessage({
                id: 'DeprecatedSavedSearch.errorLoadingSavedSearches',
                defaultMessage:
                  'An error occurred while loading Saved Searches',
                description: 'Error message when loading saved searches fails',
              })}
            </Text>
          </FlexLayout>
        )}
      </LoadingContainer>
      <DeleteSavedSearchModal />
      <DuplicateSavedSearchModal />
      <EditSavedSearchModal />
    </>
  );
};

export default Page;
